import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Search } from "lucide-react";

const empty = { name: "", email: "", password: "Doctor@123", specialty: "", qualifications: "", experience_years: 5, clinic: "", location: "", consultation_types: ["in_person"], about: "", fee: 60, gender: "male", photo: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&q=80" };

export default function AdminDoctors() {
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [d, s] = await Promise.all([
      api.get("/doctors", { params: { q, active_only: false } }),
      api.get("/specialties"),
    ]);
    setDoctors(d.data); setSpecialties(s.data);
    setLoading(false);
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/doctors", form);
      toast.success("Doctor added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (d) => {
    try { await api.patch(`/doctors/${d.id}`, { active: !d.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:items-center">
        <h1 className="text-3xl font-bold text-slate-900">Doctors</h1>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 rounded-xl h-11 w-64" data-testid="admin-doc-search" />
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-xl h-11" data-testid="add-doctor"><Plus className="w-4 h-4 mr-2" /> Add doctor</Button>
        </div>
      </div>

      <div className="doccare-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Name</th>
              <th className="p-3 font-semibold">Specialty</th>
              <th className="p-3 font-semibold hidden md:table-cell">Clinic</th>
              <th className="p-3 font-semibold hidden md:table-cell">Experience</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && doctors.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">No doctors</td></tr>}
            {doctors.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="p-3 font-semibold text-slate-900">{d.name}</td>
                <td className="p-3 text-emerald-700">{d.specialty}</td>
                <td className="p-3 hidden md:table-cell text-slate-600">{d.clinic}</td>
                <td className="p-3 hidden md:table-cell text-slate-600">{d.experience_years}y</td>
                <td className="p-3"><Badge className={`rounded-full ${d.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{d.active ? "Active" : "Inactive"}</Badge></td>
                <td className="p-3">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggle(d)} data-testid={`toggle-${d.id}`}>{d.active ? "Deactivate" : "Activate"}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add doctor</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl h-11" data-testid="new-doc-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl h-11" data-testid="new-doc-email" /></div>
            <div><Label>Password</Label><Input value={form.password} onChange={set("password")} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Specialty</Label>
              <Select value={form.specialty} onValueChange={set("specialty")}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{specialties.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Qualifications</Label><Input value={form.qualifications} onChange={set("qualifications")} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Experience (yrs)</Label><Input type="number" value={form.experience_years} onChange={(e) => set("experience_years")(Number(e.target.value))} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Clinic</Label><Input value={form.clinic} onChange={set("clinic")} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={set("location")} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Fee ($)</Label><Input type="number" value={form.fee} onChange={(e) => set("fee")(Number(e.target.value))} className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Gender</Label>
              <Select value={form.gender} onValueChange={set("gender")}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>About</Label><Textarea value={form.about} onChange={set("about")} className="mt-1.5 rounded-xl" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={create} disabled={saving || !form.name || !form.email || !form.specialty} className="rounded-xl" data-testid="save-doctor">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add doctor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
